/**
 * Crew membership display for the services area.
 *
 * A crew's members reach the screen in two shapes. The crew list sends each
 * member as a canonical reference carrying only a name, while a member just
 * picked from the member picker is a full user record with the separate name
 * parts, an email, and a customer. Both have to render in the same list and
 * the same grid cell, so they are resolved to one shape here.
 *
 * Contains:
 * - crewMemberName — a member's display name from either shape
 * - hydrateCrewMembers — swap references for full records where known
 *
 * Returns plain JS — no JSX.
 */

/**
 * Return a crew member's display name, whichever shape they arrived in.
 *
 * @param {{first_name?: string, last_name?: string, name?: string}|null|undefined} member
 * @returns {string} The member's name, or an empty string when unknown.
 */
export function crewMemberName(member) {
  if (!member) return '';
  const fullName = `${member.first_name ?? ''} ${member.last_name ?? ''}`.trim();
  return fullName || member.name || '';
}

/**
 * Resolve crew members against a directory of full user records.
 *
 * A member the directory doesn't know — someone no longer in the crew-member
 * group, say — is left as they came, so they still render by name instead of
 * dropping out of the crew.
 *
 * @param {Array<{id: number|string}>} members - The crew's members.
 * @param {Array<{id: number|string}>} directory - Known full user records.
 * @returns {Array<object>} One entry per member, in the original order.
 */
export function hydrateCrewMembers(members, directory) {
  if (!Array.isArray(members)) return [];
  const byId = new Map((directory ?? []).map(user => [user.id, user]));
  return members.map(member => byId.get(member?.id) ?? member);
}
